import { createContext, useContext, useReducer, type ReactNode } from 'react';

import type { TScheduleTriggerProgress } from '@src/models/interfaces/scheduleTrigger.interface';

import { initialState, reducer } from '../reducer';
import type {
  ActiveScheduleExecution,
  ActiveSchedulesDashboardContextValue,
  ActiveSchedulesDashboardDrawerType,
} from '../types';

const ActiveSchedulesDashboardContext = createContext<ActiveSchedulesDashboardContextValue | null>(
  null,
);

interface ActiveSchedulesDashboardProviderProps {
  children: ReactNode;
}

export const ActiveSchedulesDashboardProvider = ({
  children,
}: ActiveSchedulesDashboardProviderProps) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const toggleDrawer = (payload: ActiveSchedulesDashboardDrawerType) => {
    dispatch({ type: 'TOGGLE_DRAWER', payload });
  };

  const setSelectedSchedule = (payload: ActiveScheduleExecution | null) => {
    dispatch({ type: 'SET_SELECTED_SCHEDULE', payload });
  };

  const setSelectedTriggers = (payload: TScheduleTriggerProgress[]) => {
    dispatch({ type: 'SET_SELECTED_TRIGGERS', payload });
  };

  const value: ActiveSchedulesDashboardContextValue = {
    drawerState: state.drawerState,
    setSelectedSchedule,
    selectedSchedule: state.selectedSchedule,
    toggleDrawer,
    setSelectedTriggers,
    selectedTriggers: state.selectedTriggers,
  };

  return (
    <ActiveSchedulesDashboardContext.Provider value={value}>
      {children}
    </ActiveSchedulesDashboardContext.Provider>
  );
};

export const useActiveSchedulesDashboardContext = (): ActiveSchedulesDashboardContextValue => {
  const context = useContext(ActiveSchedulesDashboardContext);
  if (!context) {
    throw new Error(
      'useActiveSchedulesDashboardContext must be used within ActiveSchedulesDashboardProvider',
    );
  }
  return context;
};
